/**
 * User Attendees Types
 *
 * Types for saved attendee profiles that can be reused across bookings.
 * Parents can save their children's information for quick checkout.
 */

import type { Json } from './database.types'

/**
 * Form response saved with version info for detecting outdated responses
 */
export interface SavedFormResponse {
  questionId: string
  label?: string
  answer: string | string[] | boolean | number
}

/**
 * Versioned form responses - tracks when form was last updated
 * to detect if saved responses may be outdated
 */
export interface VersionedFormResponses {
  version: string // ISO timestamp of form's updated_at when responses were saved
  formName?: string // Optional form name for display
  responses: SavedFormResponse[]
}

/**
 * Saved attendee profile for a user
 */
export interface UserAttendeeView {
  id: string
  userId: string

  // Basic identification
  firstName: string
  lastName: string
  dateOfBirth: string // ISO date string
  rijksregisternummer?: string | null
  gender?: string | null

  // Medical information
  medicalInfo?: string | null
  allergies?: string | null
  medications?: string | null
  dietaryRestrictions?: string | null
  specialNeeds?: string | null

  // Emergency contact
  emergencyContactName?: string | null
  emergencyContactPhone?: string | null
  emergencyContactRelation?: string | null

  // Saved form responses keyed by form_id
  savedFormResponses: Record<string, VersionedFormResponses>

  // Metadata
  isPrimary: boolean
  nickname?: string | null

  // Timestamps
  createdAt: string
  updatedAt: string
}

/**
 * Input for creating a new attendee
 */
export interface CreateUserAttendeeInput {
  firstName: string
  lastName: string
  dateOfBirth: string
  rijksregisternummer?: string | null
  gender?: string | null
  medicalInfo?: string | null
  allergies?: string | null
  medications?: string | null
  dietaryRestrictions?: string | null
  specialNeeds?: string | null
  emergencyContactName?: string | null
  emergencyContactPhone?: string | null
  emergencyContactRelation?: string | null
  savedFormResponses?: Record<string, VersionedFormResponses>
  isPrimary?: boolean
  nickname?: string | null
}

/**
 * Input for updating an attendee (all fields optional)
 */
export interface UpdateUserAttendeeInput {
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  rijksregisternummer?: string | null
  gender?: string | null
  medicalInfo?: string | null
  allergies?: string | null
  medications?: string | null
  dietaryRestrictions?: string | null
  specialNeeds?: string | null
  emergencyContactName?: string | null
  emergencyContactPhone?: string | null
  emergencyContactRelation?: string | null
  savedFormResponses?: Record<string, VersionedFormResponses>
  isPrimary?: boolean
  nickname?: string | null
}

/**
 * Database row format (snake_case)
 */
export interface UserAttendeeRow {
  id: string
  user_id: string
  first_name: string
  last_name: string
  date_of_birth: string
  rijksregisternummer: string | null
  gender: string | null
  medical_info: string | null
  allergies: string | null
  medications: string | null
  dietary_restrictions: string | null
  special_needs: string | null
  emergency_contact_name: string | null
  emergency_contact_phone: string | null
  emergency_contact_relation: string | null
  saved_form_responses: Json | null
  is_primary: boolean | null
  nickname: string | null
  created_at: string | null
  updated_at: string | null
}

/**
 * Utility to convert database row to frontend model
 */
export function rowToUserAttendee(row: UserAttendeeRow): UserAttendeeView {
  return {
    id: row.id,
    userId: row.user_id,
    firstName: row.first_name,
    lastName: row.last_name,
    dateOfBirth: row.date_of_birth,
    rijksregisternummer: row.rijksregisternummer,
    gender: row.gender,
    medicalInfo: row.medical_info,
    allergies: row.allergies,
    medications: row.medications,
    dietaryRestrictions: row.dietary_restrictions,
    specialNeeds: row.special_needs,
    emergencyContactName: row.emergency_contact_name,
    emergencyContactPhone: row.emergency_contact_phone,
    emergencyContactRelation: row.emergency_contact_relation,
    savedFormResponses: (row.saved_form_responses as unknown as Record<string, VersionedFormResponses>) || {},
    isPrimary: row.is_primary ?? false,
    nickname: row.nickname,
    createdAt: row.created_at || new Date().toISOString(),
    updatedAt: row.updated_at || new Date().toISOString(),
  }
}

/**
 * Utility to convert frontend model to database row format
 */
export function userAttendeeToRow(
  attendee: CreateUserAttendeeInput | UpdateUserAttendeeInput,
  userId?: string,
): Partial<UserAttendeeRow> {
  const row: Partial<UserAttendeeRow> = {}

  if (userId)
    row.user_id = userId
  if (attendee.firstName !== undefined)
    row.first_name = attendee.firstName
  if (attendee.lastName !== undefined)
    row.last_name = attendee.lastName
  if (attendee.dateOfBirth !== undefined)
    row.date_of_birth = attendee.dateOfBirth
  if (attendee.rijksregisternummer !== undefined)
    row.rijksregisternummer = attendee.rijksregisternummer
  if (attendee.gender !== undefined)
    row.gender = attendee.gender
  if (attendee.medicalInfo !== undefined)
    row.medical_info = attendee.medicalInfo
  if (attendee.allergies !== undefined)
    row.allergies = attendee.allergies
  if (attendee.medications !== undefined)
    row.medications = attendee.medications
  if (attendee.dietaryRestrictions !== undefined)
    row.dietary_restrictions = attendee.dietaryRestrictions
  if (attendee.specialNeeds !== undefined)
    row.special_needs = attendee.specialNeeds
  if (attendee.emergencyContactName !== undefined)
    row.emergency_contact_name = attendee.emergencyContactName
  if (attendee.emergencyContactPhone !== undefined)
    row.emergency_contact_phone = attendee.emergencyContactPhone
  if (attendee.emergencyContactRelation !== undefined)
    row.emergency_contact_relation = attendee.emergencyContactRelation
  if (attendee.savedFormResponses !== undefined)
    row.saved_form_responses = attendee.savedFormResponses as unknown as Json
  if (attendee.isPrimary !== undefined)
    row.is_primary = attendee.isPrimary
  if (attendee.nickname !== undefined)
    row.nickname = attendee.nickname

  return row
}

/**
 * Mask rijksregisternummer for display (show only last 4 characters)
 * Example: "90.01.01-123.45" -> "••.••.••-•••.45"
 */
export function maskRijksregisternummer(rrn: string | null | undefined): string {
  if (!rrn)
    return ''

  // Keep only the last 2 digits visible
  const cleaned = rrn.replace(/\D/g, '')
  if (cleaned.length < 2)
    return '••.••.••-•••.••'

  const lastTwo = cleaned.slice(-2)

  return `••.••.••-•••.${lastTwo}`
}

/**
 * Get display name for attendee (nickname or full name)
 */
export function getAttendeeDisplayName(attendee: UserAttendeeView): string {
  if (attendee.nickname)
    return attendee.nickname

  return `${attendee.firstName} ${attendee.lastName}`
}

/**
 * Calculate age from date of birth
 */
export function calculateAge(dateOfBirth: string): number {
  const today = new Date()
  const birthDate = new Date(dateOfBirth)
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()))
    age--

  return age
}
